import { z } from 'zod/v4'

export const WorkspaceStatusValidator = z.enum(['active', 'archived'])
export const WorkspaceRoleValidator = z.enum(['owner', 'editor', 'viewer'])

export const WorkspaceValidator = z.object({
	id: z.number(),
	uuid: z.string(),
	account_id: z.string(),
	background_image: z.string().nullable(),
	created_at: z.iso.datetime(),
	name: z.string(),
	status: WorkspaceStatusValidator,
	campaign_count: z.number(),
	connection_count: z.number(),
	grid_count: z.number(),
	landing_page_count: z.number(),
	location_count: z.number(),
})

export const BaseWorkspaceValidator = WorkspaceValidator.omit({
	id: true,
	uuid: true,
	created_at: true,
}).partial()

export const WorkspacePermissionsValidator = z.object({
	id: z.number(),
	uuid: z.string(),
	workspace_id: z.number(),
	user_id: z.string().optional(),
	email: z.string(),
	role: WorkspaceRoleValidator,
	invitation_status: z.enum(['pending', 'accepted']).default('pending'),
	created_at: z.iso.datetime(),
})

export const BaseWorkspacePermissionsValidator = WorkspacePermissionsValidator.omit({
	id: true,
	uuid: true,
	created_at: true,
})

export const InviteUserValidator = z.object({
	email: z.string(),
	role: WorkspaceRoleValidator,
})

export type WorkspaceId = z.infer<typeof WorkspaceValidator.shape.uuid>
export type WorkspaceStatus = z.infer<typeof WorkspaceStatusValidator>
export type WorkspaceRole = z.infer<typeof WorkspaceRoleValidator>
export type Workspace = z.infer<typeof WorkspaceValidator>
export type BaseWorkspace = z.infer<typeof BaseWorkspaceValidator>
export type WorkspacePermission = z.infer<typeof WorkspacePermissionsValidator>
export type BaseWorkspacePermission = z.infer<typeof BaseWorkspacePermissionsValidator>
export type InviteUser = z.infer<typeof InviteUserValidator>
// Asset types that were in Workspace.ts
export enum AssetTypes {
	IMAGE = 'image',
	VIDEO = 'video',
	HEADLINE = 'headline',
	DESCRIPTION = 'description',
	CTA = 'cta',
}

export const IntegrationValidator = z.object({
	platform: z.enum(['google', 'facebook']),
	refresh_token: z.string(),
	access_token: z.string(),
})

export const AssetLibraryValidator = z.object({
	id: z.string().optional(),
	name: z.string(),
	headlines: z.array(z.string()),
})

export const AssetValidator = z.object({
	id: z.string(),
	url: z.string(),
	type: z.nativeEnum(AssetTypes),
	tags: z.array(z.string()),
})

export type Integration = z.infer<typeof IntegrationValidator>
export type AssetLibrary = z.infer<typeof AssetLibraryValidator>
export type Asset = z.infer<typeof AssetValidator>
